const STOREFRONT_API_ROOT = (process.env.VUE_APP_STOREFRONT_API_ROOT || 'http://127.0.0.1:8000/api/storefront').replace(/\/$/, '');

function buildUrl(path, params = {}) {
  const url = new URL(`${STOREFRONT_API_ROOT}${path}`);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

async function parseResponse(response) {
  const text = await response.text();
  let payload = {};

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch (error) {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    const error = new Error(payload.message || 'Request failed.');
    error.status = response.status;
    error.errors = payload.errors || {};
    error.payload = payload;
    // How long the server really wants us to wait — a throttled request says so in the header.
    const retryAfter = Number(response.headers.get('Retry-After'));
    if (retryAfter > 0) error.retryAfter = retryAfter;
    throw error;
  }

  return payload;
}

const TOKEN_KEY = 'sweet_home_client_token';

// Public endpoints don't require a session, but sending the token when we have one
// lets the server attach an order or a custom request to the signed-in account —
// it no longer accepts a customer id from the request body.
function storedToken() {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.localStorage.getItem(TOKEN_KEY) || '';
}

/**
 * Requests that have been sent but have not answered yet, keyed by what makes two
 * of them identical. The navbar and the product grid both bootstrap the session
 * and the wishlist from their own onMounted, and the home page mounts the grid
 * twice — six requests for two answers, because every guard those composables
 * keep is set after its own await and so is still false when the second caller
 * looks at it.
 *
 * This is deliberately not a cache: the entry is removed the moment the response
 * settles, so it can only ever join callers that were already waiting on the same
 * request at the same time. A later request always goes to the network, which is
 * what keeps prices and stock live.
 */
const inFlight = new Map();

// A stable id for this device, used by the AI assistant's fair-use limits for visitors who are not
// signed in. It is a hash of things the browser reports about itself (engine, platform, screen,
// language, timezone) plus a random id kept in storage — not a MAC address, which no website can read.
const DEVICE_KEY = 'sh_device_seed';
let deviceIdPromise = null;

async function sha256Hex(text) {
  if (window.crypto?.subtle && window.TextEncoder) {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Very old browsers: a small non-cryptographic fallback, still 16+ hex chars.
  let h1 = 0x811c9dc5;
  let h2 = 0x1b873593;
  for (let i = 0; i < text.length; i += 1) {
    h1 = Math.imul(h1 ^ text.charCodeAt(i), 16777619) >>> 0;
    h2 = Math.imul(h2 ^ text.charCodeAt(i), 2246822519) >>> 0;
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).repeat(2);
}

export function deviceId() {
  if (typeof window === 'undefined') return Promise.resolve('');
  if (!deviceIdPromise) {
    deviceIdPromise = (async () => {
      let seed = '';
      try {
        seed = window.localStorage.getItem(DEVICE_KEY) || '';
        if (!seed) {
          seed = Math.random().toString(36).slice(2) + Date.now().toString(36);
          window.localStorage.setItem(DEVICE_KEY, seed);
        }
      } catch (e) { /* storage blocked: fingerprint only */ }
      const n = window.navigator || {};
      const parts = [
        n.userAgent, n.language, n.platform, n.hardwareConcurrency, n.maxTouchPoints,
        window.screen?.width, window.screen?.height, window.screen?.colorDepth, window.devicePixelRatio,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      ];
      return sha256Hex(parts.join('|'));
    })().catch(() => '');
  }
  return deviceIdPromise;
}

export async function storefrontRequest(path, { method = 'GET', params, data, headers = {} } = {}) {
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const token = storedToken();
  const device = await deviceId();
  const requestHeaders = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(device ? { 'X-Device-Id': device } : {}),
    ...headers,
  };

  if (data && !isFormData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const url = buildUrl(path, params);

  // Only reads. A POST is an action — two of them are two orders, never one.
  const shareKey = method === 'GET' && !data ? `${token}\n${url}` : null;

  if (shareKey && inFlight.has(shareKey)) {
    return inFlight.get(shareKey);
  }

  const pending = fetch(url, {
    method,
    headers: requestHeaders,
    body: !data
      ? undefined
      : isFormData
        ? data
        : JSON.stringify(data),
  }).then(parseResponse);

  if (!shareKey) {
    return pending;
  }

  inFlight.set(shareKey, pending);

  try {
    return await pending;
  } finally {
    inFlight.delete(shareKey);
  }
}

/**
 * Fetch a file (not JSON) from the API with the same auth and device headers, and read the filename
 * the server suggested. Returns { data: Blob, filename: string }.
 */
export async function storefrontBlob(path, params) {
  const token = storedToken();
  const device = await deviceId();
  const response = await fetch(buildUrl(path, params), {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(device ? { 'X-Device-Id': device } : {}),
    },
  });

  if (!response.ok) {
    const error = new Error('The file could not be downloaded.');
    error.status = response.status;
    throw error;
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

  return { data: await response.blob(), filename: match ? decodeURIComponent(match[1]) : '' };
}

export function formatPrice(value, currency = 'USD') {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

export function mapCategory(category) {
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    description: category.description || '',
    image: category.image_url || '',
    // Already rolled up over the sub categories by the API, so it matches what the
    // shop grid shows once ?category=<id> is applied.
    productCount: Number(category.products_count || 0),
    children: (category.children || []).map((child) => ({
      id: child.id,
      name: child.name,
      slug: child.slug,
      description: child.description || '',
      image: child.image_url || '',
      productCount: Number(child.products_count || 0),
    })),
  };
}

// Every entry point into the shop that means "show me this category" builds the same
// link, so the grid, the home tiles and the navbar can never drift into disagreeing
// about which query parameter the shop reads.
export function shopCategoryLink(category) {
  return { path: '/shop-v2', query: { category: category.id } };
}

// The card and the detail page read the same AR fields, and the detail page normalises its
// payload locally — one mapper keeps the two from drifting apart.
export function mapProductAr(product) {
  return {
    hasAr: Boolean(product.has_ar),
    modelGlbUrl: product.model_glb_url || '',
    modelUsdzUrl: product.model_usdz_url || '',
    arPlacement: product.ar_placement || 'floor',
    arScale: product.ar_scale || 'fixed',
  };
}

export function mapProductCard(product) {
  const basePrice = Number(product.base_price || 0);
  const salePrice = product.sale_price !== null && product.sale_price !== undefined && product.sale_price !== ''
    ? Number(product.sale_price)
    : null;
  const effectivePrice = salePrice !== null && salePrice >= 0 && salePrice < basePrice
    ? salePrice
    : basePrice;
  const discountPercent = salePrice !== null && basePrice > 0 && salePrice < basePrice
    ? Math.round(((basePrice - salePrice) / basePrice) * 100)
    : 0;

  return {
    id: product.id,
    name: product.name,
    image: product.main_image_url || product.gallery_image_urls?.[0] || '',
    price: formatPrice(effectivePrice),
    originalPrice: discountPercent ? formatPrice(basePrice) : '',
    numericPrice: effectivePrice,
    tag: discountPercent ? `${discountPercent}% OFF` : (product.is_featured ? 'NEW' : ''),
    tagTone: discountPercent ? 'sale' : (product.is_featured ? 'featured' : ''),
    salePrice: salePrice,
    basePrice: basePrice,
    shortDescription: product.short_description || '',
    categoryName: product.category?.name || '',
    subCategoryName: product.sub_category?.name || product.subCategory?.name || '',
    sku: product.sku || '',
    stockQuantity: Number(product.stock_quantity || 0),
    inStock: Number(product.stock_quantity || 0) > 0,
    ...mapProductAr(product),
    raw: product,
  };
}
